"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Activity,
  AppWindow,
  Bookmark,
  BookOpen,
  Braces,
  BrainCircuit,
  ChevronDown,
  Code2,
  Database,
  FileQuestion,
  FlaskConical,
  Gauge,
  GraduationCap,
  LogOut,
  Menu,
  Route,
  TestTube2,
  X,
  type LucideIcon,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { displayName, useSession } from "@/lib/auth/session";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useNotifications } from "@/lib/notifications/context";
import { usePlatformSettings } from "@/lib/settings/context";
import { BrandLogo } from "./brand-logo";
import { GlobalSearch } from "./global-search";

type MenuEntry = {
  label: string;
  href: string;
  icon: LucideIcon;
};

type MenuSection = {
  heading: string;
  items: MenuEntry[];
};

type NavItem = {
  label: string;
  href: string;
  sections?: MenuSection[];
};

const courseSections: MenuSection[] = [
  {
    heading: "Playwright Tracks",
    items: [
      { label: "Playwright + TypeScript", href: "/courses/playwright-typescript", icon: BookOpen },
      { label: "Playwright + JavaScript", href: "/courses/playwright-javascript", icon: Code2 },
    ],
  },
  {
    heading: "Selenium Tracks",
    items: [
      { label: "Selenium + Java", href: "/courses/selenium-java", icon: AppWindow },
      { label: "Selenium + Python", href: "/courses/selenium-python", icon: FlaskConical },
    ],
  },
  {
    heading: "All Curriculums",
    items: [
      { label: "Explore All Courses", href: "/courses", icon: GraduationCap },
    ],
  },
];

const interviewSections: MenuSection[] = [
  {
    heading: "Question banks",
    items: [
      { label: "All Topics", href: "/interview", icon: FileQuestion },
      { label: "Playwright", href: "/interview/playwright", icon: AppWindow },
      { label: "Selenium", href: "/interview/selenium", icon: AppWindow },
      { label: "Cypress", href: "/interview/cypress", icon: AppWindow },
      { label: "API Testing", href: "/interview/api-testing", icon: Braces },
    ],
  },
  {
    heading: "Fundamentals & languages",
    items: [
      { label: "Manual Testing", href: "/interview/manual-testing", icon: FileQuestion },
      { label: "SQL & DB Testing", href: "/interview/sql", icon: Braces },
      { label: "Java · Python · JS · TS · C#", href: "/interview/java", icon: Braces },
      { label: "Automation Real-Time", href: "/interview/automation-real-time", icon: FileQuestion },
    ],
  },
];

const compilerSections: MenuSection[] = [
  {
    heading: "Online Compilers",
    items: [
      { label: "Playwright Online Compiler", href: "/playwright-compiler", icon: Code2 },
      { label: "Selenium Online Compiler", href: "/selenium-compiler", icon: AppWindow },
    ],
  },
  {
    heading: "Cloud Workspaces",
    items: [
      { label: "In-Browser Test IDE", href: "/ide", icon: FlaskConical },
      { label: "LeetCode for QA", href: "/leetcode-for-qa", icon: GraduationCap },
    ],
  },
];

const difficultySection: MenuSection = {
  heading: "By difficulty",
  items: [
    { label: "Easy challenges", href: "/challenges?level=easy", icon: Gauge },
    { label: "Medium challenges", href: "/challenges?level=medium", icon: Activity },
    { label: "Hard challenges", href: "/challenges?level=hard", icon: FlaskConical },
  ],
};

const mobileNavItems: NavItem[] = [
  {
    label: "Practice",
    href: "/practice",
    sections: [
      {
        heading: "Frameworks",
        items: [
          { label: "Selenium", href: "/selenium", icon: AppWindow },
          { label: "Playwright", href: "/playwright", icon: Code2 },
          { label: "Cypress", href: "/cypress", icon: TestTube2 },
          { label: "API Testing", href: "/api-testing", icon: Braces },
          { label: "AI Testing", href: "/ai-testing", icon: BrainCircuit },
          { label: "Database Testing", href: "/database-testing", icon: Database },
          { label: "Databases (SQL & NoSQL)", href: "/databases", icon: Database },
          { label: "Performance Testing", href: "/performance-testing", icon: Gauge },
        ],
      },
      difficultySection,
    ],
  },
  {
    label: "Courses",
    href: "/courses",
    sections: courseSections,
  },
  { label: "Challenges", href: "/challenges" },
  {
    label: "Compilers",
    href: "/ide",
    sections: compilerSections,
  },
  {
    label: "Interview Prep",
    href: "/interview",
    sections: interviewSections,
  },
  { label: "Pricing", href: "/pricing" },
  { label: "QA Jobs", href: "/qa-jobs" },
];

const desktopNavItems: NavItem[] = [
  { label: "Playwright", href: "/playwright" },
  { label: "Selenium", href: "/selenium" },
  { label: "API Testing", href: "/api-testing" },
  {
    label: "Compilers",
    href: "/ide",
    sections: compilerSections,
  },
  { label: "Challenges", href: "/challenges" },
  {
    label: "Interview Prep",
    href: "/interview",
    sections: interviewSections,
  },
  {
    label: "Courses",
    href: "/courses",
    sections: courseSections,
  },
  { label: "Pricing", href: "/pricing" },
  {
    label: "More",
    href: "/practice",
    sections: [
      {
        heading: "Frameworks",
        items: [
          { label: "Cypress", href: "/cypress", icon: TestTube2 },
          { label: "AI Testing", href: "/ai-testing", icon: BrainCircuit },
          { label: "Database Testing", href: "/database-testing", icon: Database },
          { label: "Databases (SQL & NoSQL)", href: "/databases", icon: Database },
        ],
      },
      difficultySection,
    ],
  },
  { label: "QA Jobs", href: "/qa-jobs" },
];

function NavChevron({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    >
      <path d="M6 10C6 6.72 3.32 4.06 0 4.06M6 9.94C6 6.66 8.68 4 12 4" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function PanelItem({ entry, onClose }: { entry: MenuEntry; onClose: () => void }) {
  const Icon = entry.icon;
  return (
    <Link
      prefetch={false}
      href={entry.href}
      onClick={onClose}
      role="menuitem"
      tabIndex={0}
      className="group/item flex items-center gap-3 rounded-[8px] px-1 py-1 transition hover:bg-[#e8fedf] focus:bg-[#e8fedf] focus:outline-none"
    >
      <span className="icon-tile h-7 w-7" style={{ ["--arm" as string]: "6px" }}>
        <Icon size={16} strokeWidth={1.8} />
      </span>
      <span className="text-[16px] font-normal leading-6 text-[#1b1b1b]">{entry.label}</span>
    </Link>
  );
}

function DesktopMenu({ item, isOpen, isActive, onOpen, onClose }: { item: NavItem; isOpen: boolean; isActive: boolean; onOpen: () => void; onClose: () => void }) {
  // Nav-link hover treatment shared by both variants: white pill, rounded, subtle shadow.
  const linkClass =
    "flex items-center gap-1 rounded-[12px] border-[1.6px] border-transparent px-1.5 py-2 font-display text-[13.5px] font-semibold leading-6 tracking-[-.01em] text-[#1b1b1b] transition hover:border-black/0 hover:bg-white hover:shadow-[0_1px_2px_rgba(27,27,27,.08)] xl:gap-1.5 xl:px-2.5 xl:text-[15px] 2xl:px-3.5 2xl:text-[16.5px] whitespace-nowrap";

  if (!item.sections) {
    return (
      <Link
        prefetch={false}
        href={item.href}
        onMouseEnter={onClose}
        className={`${linkClass} ${isActive ? "bg-white/85 shadow-[0_1px_2px_rgba(27,27,27,.08)]" : ""}`}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div className="relative" onMouseEnter={onOpen}>
      <button
        type="button"
        onClick={isOpen ? onClose : onOpen}
        onFocus={onOpen}
        className={`${linkClass} ${isOpen ? "border-black/0 bg-white shadow-[0_1px_2px_rgba(27,27,27,.08)]" : ""}`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {item.label}
        <NavChevron open={isOpen} />
      </button>
      {isOpen && (
        <div className={`absolute top-full z-50 pt-3 ${item.label === "More" ? "right-0" : "left-0"}`}>
          <div className="overflow-hidden rounded-[16px] border border-black/10 bg-white shadow-[0_18px_45px_rgba(19,42,29,.14)]" role="menu">
            <div className="flex max-w-[1000px] gap-8 p-6">
              {item.sections.map((section, index) => (
                <div key={section.heading} className={`w-[240px] ${index > 0 ? "border-l border-black/[.14] pl-8" : ""}`}>
                  <p className="mb-3 font-display text-[18px] font-semibold leading-6 text-[#696969]">{section.heading}</p>
                  <div className="flex flex-col gap-2">
                    {section.items.map((entry) => (
                      <PanelItem key={`${entry.href}-${entry.label}`} entry={entry} onClose={onClose} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MobileMenuItem({ item, expanded, onToggle, onNavigate }: { item: NavItem; expanded: boolean; onToggle: () => void; onNavigate: () => void }) {
  if (!item.sections) {
    return <Link prefetch={false} href={item.href} onClick={onNavigate} className="block rounded-[9px] px-3 py-3 text-[15px] font-semibold tracking-[-.01em] hover:bg-black/[.055]">{item.label}</Link>;
  }

  return (
    <div className="border-b border-black/8 last:border-0">
      <button type="button" onClick={onToggle} className="flex w-full items-center justify-between rounded-[9px] px-3 py-3 text-left text-[15px] font-semibold tracking-[-.01em] hover:bg-black/[.055]" aria-expanded={expanded}>
        {item.label}
        <ChevronDown size={14} strokeWidth={2.2} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="mb-2 ml-3 grid gap-2 border-l-2 border-green/25 pl-3">
          {item.sections.map((section) => (
            <div key={section.heading}>
              <p className="px-3 pb-1 pt-2 font-display text-[13px] font-semibold text-[#696969]">{section.heading}</p>
              {section.items.map((entry) => {
                const Icon = entry.icon;
                return (
                  <Link prefetch={false} key={`${entry.href}-${entry.label}`} href={entry.href} onClick={onNavigate} className="group/item flex items-center gap-3 rounded-[8px] px-3 py-2.5 hover:bg-[#e8fedf]">
                    <span className="icon-tile h-7 w-7" style={{ ["--arm" as string]: "6px" }}>
                      <Icon size={15} strokeWidth={1.8} />
                    </span>
                    <span className="text-[16px] font-normal leading-6 text-[#1b1b1b]">{entry.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PublicNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopMenu, setDesktopMenu] = useState<string | null>(null);
  const [mobileMenu, setMobileMenu] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const { status, user, signOut } = useSession();
  const { unreadCount } = useNotifications();
  const { isCoursesEnabled } = usePlatformSettings();
  const isHome = pathname === "/";

  const visibleDesktopItems = desktopNavItems.filter((item) => isCoursesEnabled || item.href !== "/courses");
  const visibleMobileItems = mobileNavItems.filter((item) => isCoursesEnabled || item.href !== "/courses");

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setDesktopMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDesktopMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileMenu(null);
  };

  return (
    <>
      <a href="#main-content" className="fixed left-4 top-2 z-[120] -translate-y-20 rounded-[10px] bg-ink px-4 py-2 text-sm font-semibold text-white focus:translate-y-0">Skip to content</a>
      <header className={`sticky top-0 z-50 transition-colors bg-lime ${isHome ? "border-b border-transparent" : "border-b border-black/10"}`}>
        <div className="mx-auto flex h-[72px] max-w-[1520px] items-center gap-2 px-4 sm:px-6 lg:h-[91px] lg:px-5 xl:gap-3 xl:px-10 2xl:px-12">
          <BrandLogo />
          <div ref={navRef} className="ml-1 hidden items-center lg:flex xl:ml-3" onMouseLeave={() => setDesktopMenu(null)}>
            <nav className="flex items-center" aria-label="Primary navigation">
              {visibleDesktopItems.map((item) => (
                <DesktopMenu
                  key={item.label}
                  item={item}
                  isOpen={desktopMenu === item.label}
                  isActive={pathname === item.href || (item.href !== "/" && pathname.startsWith(`${item.href}/`))}
                  onOpen={() => setDesktopMenu(item.label)}
                  onClose={() => setDesktopMenu(null)}
                />
              ))}
            </nav>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <GlobalSearch compact />
            <span className="mx-1 hidden h-9 w-px bg-black/15 sm:block" />
            {status === "authenticated" ? (
              <div className="hidden items-center gap-1.5 sm:flex">
                <Link
                  prefetch={false}
                  href="/bookmarks"
                  className="flex h-10 w-10 items-center justify-center rounded-full text-ink/75 transition hover:bg-black/[.06] hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                  title="Your bookmarks"
                  aria-label="Your bookmarks"
                >
                  <Bookmark size={17} strokeWidth={2} aria-hidden="true" />
                </Link>
                <div className="flex h-11 items-center rounded-full border border-black/10 bg-white/55 p-1 shadow-[0_1px_2px_rgba(27,27,27,.06)] backdrop-blur-sm">
                  <Link
                    prefetch={false}
                    href="/profile"
                    className="flex h-9 items-center gap-2.5 rounded-full pl-0.5 pr-3 transition hover:bg-white/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                    aria-label={`Account profile${unreadCount > 0 ? `, ${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}` : ""}`}
                  >
                    <span
                      className="relative flex h-8 w-8 items-center justify-center rounded-full ring-2 ring-white shadow-[0_2px_6px_-1px_rgba(15,92,49,.45)]"
                      aria-hidden="true"
                    >
                      <UserAvatar user={user} size={32} />
                      {unreadCount > 0 && (
                        <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#e5484d] px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white">
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </span>
                    <span className="hidden max-w-[140px] text-left md:block">
                      <span className="block truncate text-[13px] font-semibold leading-4 text-ink">{displayName(user)}</span>
                      <span className="block text-[11px] leading-4 text-ink/55">View profile</span>
                    </span>
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <Link prefetch={false} href="/login" className="hidden h-10 items-center justify-center rounded-[12px] border-[1.6px] border-transparent px-3.5 py-1.5 text-[14px] font-medium leading-5 tracking-[-.01em] text-[#1b1b1b] underline decoration-black/40 underline-offset-[3px] transition hover:text-green sm:inline-flex">Log in</Link>
                <Link prefetch={false} href="/signup" className="hidden h-10 items-center justify-center rounded-[12px] border-[1.6px] border-[#1b1b1b] bg-[#1b1b1b] px-3.5 py-1.5 text-[14px] font-medium leading-5 text-white shadow-[inset_-1px_-3px_7.8px_rgba(27,27,27,.25),inset_1px_3px_8px_rgba(255,255,255,.2),0_4px_8px_-2px_rgba(27,27,27,.14),0_2px_4px_-2px_rgba(27,27,27,.1)] transition hover:bg-[#2a2a2a] sm:inline-flex">Sign Up Free</Link>
              </>
            )}
            <button type="button" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] hover:bg-black/[.055] lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} aria-expanded={mobileOpen} aria-controls="mobile-navigation" aria-label={mobileOpen ? "Close navigation" : "Open navigation"}>{mobileOpen ? <X size={20} /> : <Menu size={20} />}</button>
          </div>
        </div>
        {mobileOpen && (
          <nav id="mobile-navigation" className="border-t border-black/10 bg-lime px-4 py-3 lg:hidden" aria-label="Mobile navigation">
            <div className="mx-auto max-w-[1440px]">
              {visibleMobileItems.map((item) => (
                <MobileMenuItem
                  key={item.label}
                  item={item}
                  expanded={mobileMenu === item.label}
                  onToggle={() => setMobileMenu(mobileMenu === item.label ? null : item.label)}
                  onNavigate={closeMobile}
                />
              ))}
              {status === "authenticated" ? (
                <div className="mt-3 grid grid-cols-2 gap-2 border-t border-black/10 pt-4">
                  <Link prefetch={false} href="/profile" className="btn-secondary h-11 text-sm relative flex items-center justify-center gap-2" onClick={closeMobile}>
                    Profile
                    {unreadCount > 0 && (
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#d92d20] px-1.5 text-[10px] font-black leading-none text-white">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </Link>
                  <button type="button" className="btn-primary h-11 text-sm" onClick={() => { closeMobile(); signOut(); router.push("/"); }}>Sign out</button>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2 border-t border-black/10 pt-4">
                  <Link prefetch={false} href="/login" className="btn-secondary h-11 text-sm" onClick={closeMobile}>Log in</Link>
                  <Link prefetch={false} href="/signup" className="btn-primary h-11 text-sm" onClick={closeMobile}>Start free</Link>
                </div>
              )}
            </div>
          </nav>
        )}
      </header>
    </>
  );
}
